import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { track } from "analytics/usageTracker";
import { FEATURE_ACTIVITY_DELETED, FEATURE_ADD_LEAVE } from "analytics/featureCatalog";
import { Card, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  readSessionCurrentClient,
  readSessionCurrentMember,
  getBdmClients,
  getCurrentManager,
  readSessionCurrentManager,
  getMemberClients,
} from "app/services/app.state.service";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useManagerScopeOptions } from "app/hooks/useManagerScopeOptions";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import { toClientListOptions, toMemberClientOptions } from "utils/clientOptions";
import { parseReportRowEntityId } from "utils/reportRowId";
import { withdrawHolidayElectionInBackend } from "app/services/holiday.service";
import {
  MANAGER_OPTION_PREFIX,
  deriveSubtreeClients,
  deriveSubtreeL1Options,
  mergeManagerScope,
  siblingRecordIds,
} from "utils/managerSubtree";
import { getDateInFormat } from "utils/dateUtil";
import { loadUnifiedEfforts } from "app/services/report.service";
import { loadAllClients } from "app/services/client.service";
import { handleAxiosError } from "app/services/app.error.handler";
import { ReportDataGridBase } from "./ReportDataGridBase";
import {
  ALL_CLIENTS,
  ALL_USERS,
  MANAGER_SELF_OPTION_VALUE,
  ROLE_ADMIN,
  ROLE_BDM,
  ROLE_CLIENT,
  ROLE_EMPLOYEE_MANAGER,
  ROLE_EMPLOYEE_MEMBER,
  OWNER_TYPE_MEMBER,
  OWNER_TYPE_MANAGER,
} from "AppConstants";
import {
  deleteLeavePlan,
  loadMembersFromBackendForClient,
  loadMembersForAllClientsFromBackend,
  getLeavePlanById,
} from "app/services/member.service";
import { loadMembersFromBackendForManager, loadAllManagersFromBackend, loadScopeManagersForIds } from "app/services/manager.service";
import { Stack } from "@mui/system";
import AddActivityDialog from "./AddActivityDialog";
import { Add as AddIcon, AddMultiple as AddMultipleIcon, Timelapse as TimelapseIcon } from "components/icons";
import AddLeaveDialog from "./AddLeaveDialog";
import { ConfirmDialog } from "../ConfirmDialog";
import { deleteActivity, loadAllActivityTypesByClient } from "app/services/activity.stats.service";
import { useDispatch, useSelector } from "react-redux";
import { reportActions } from "store/report-slice";
import { clientActions } from "store/client-slice";

// Re-exported from the shared module so existing tests that import these helpers
// from this component keep resolving. New code should import from utils/managerSubtree.
export { MANAGER_OPTION_PREFIX, deriveSubtreeClients, deriveSubtreeL1Options };

// Delete-confirmation heading per row type. LEAVE_PLAN also serves as the fallback.
const DELETE_DIALOG_TITLES = {
  ACTIVITY: "Delete Activity?",
  HOLIDAY: "Remove Optional Holiday?",
  LEAVE_PLAN: "Delete Leave Plan?",
};

export const ConsolidatedReportFilter = ({ role, members }) => {
  // A BDM scopes this report by client exactly as an Admin does -- HRMS-116 point 3, "see reports
  // for the clients they are assigned to (like Managers)", which in this component's terms is the
  // client-scoped path, not the manager-scoped one (a BDM holds no _manager record to root at).
  // The only difference is where the client list comes from: an Admin gets every client, a BDM
  // gets the accounts naming them as relationship manager.
  // @since NEXT_VERSION
  const isClientScoped = role === ROLE_ADMIN || role === ROLE_BDM;
  const [clients, setClients] = useState([]);
  const [clientMembers, setClientMembers] = useState([]);
  // All managers (Admin only) — used to offer Managers as selectable options in the
  // Member dropdown so an Admin can scope the report to a single manager.
  const [adminManagers, setAdminManagers] = useState([]);
  // The logged-in manager's subtree (self + L1 children). An L1 gets just themselves.
  const [subtreeManagers, setSubtreeManagers] = useState([]);
  const [managerClients, setManagerClients] = useState([]);
  const [memberClientsList, setMemberClientsList] = useState([]);
  const [memberClientsLoaded, setMemberClientsLoaded] = useState(false);

  const [addActivityOpen, setAddActivityOpen] = useState(false);
  // Which activity screen the dialog opens on: the table, or the single-activity form that Add,
  // Edit and Copy all use. @since NEXT_VERSION
  const [activityBulkMode, setActivityBulkMode] = useState(false);
  const [addLeaveOpen, setAddLeaveOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [copyObject, setCopyObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [objectType, setObjectType] = useState(null);
  // Withdrawing an optional holiday is keyed by its owner, not by a row id.
  const [deleteOwner, setDeleteOwner] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  // The logged-in manager's _manager.manager_id (distinct from a client id).
  // Used to call the manager-scoped report endpoint.
  const [selfManagerId, setSelfManagerId] = useState(null);
  const [selfManagerName, setSelfManagerName] = useState("");

  const reportData = useSelector((state) => state.report.rows);
  const filterStartDate = useSelector((state) => state.report.filter.startDate);
  const filterEndDate = useSelector((state) => state.report.filter.endDate);
  const filterOption = useSelector((state) => state.report.filter.searchOption);
  const filterYear = useSelector((state) => state.report.filter.year);
  const filterMonth = useSelector((state) => state.report.filter.month);
  const filterWeek = useSelector((state) => state.report.filter.week);
  const prevSelectedClient = useSelector((state) => state.report.filter.client);
  const prevSelectedMember = useSelector((state) => state.report.filter.member);
  const expectedWorkingHours = useSelector((state) => state.report.hours.expected);
  const totalHours = useSelector((state) => state.report.hours.total);
  // Per-client reference caches warmed on dashboard load (see report.prefetch.service).
  // Read these before hitting the network so dropdown changes don't re-fetch.
  const membersByClient = useSelector((state) => state.client.membersByClient || {});
  const typesByClient = useSelector((state) => state.client.typesByClient || {});

  const [selectedClient, setSelectedClient] = useState(prevSelectedClient);
  const [selectedMember, setSelectedMember] = useState(prevSelectedMember ?? ALL_USERS);
  // The whole period travels as one value now instead of six pieces of state, seeded from
  // whatever the slice remembers so returning to the report reopens the last-applied range.
  const [period, setPeriod] = useState(() =>
    defaultPeriod({
      searchOption: filterOption,
      year: filterYear,
      month: filterMonth,
      fiscalWeek: filterWeek,
      customStart: filterStartDate,
      customEnd: filterEndDate,
    })
  );

  const parseNullableInt = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  // Every manager record the viewer holds — one per client. Decides which rows are their own,
  // so a manager is recognised under all their clients, not just the first. @since NEXT_VERSION
  const selfManagerIds = useMemo(
    () =>
      managerClients
        .map((record) => parseInt(record?.id, 10))
        .filter((id) => !Number.isNaN(id)),
    [managerClients]
  );

  const dedupeById = (rows) => {
    const seen = new Set();
    return rows.filter((r) => {
      const key = r?.id ?? JSON.stringify(r);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // All membership records for a selected person (the Member dropdown value is a
  // userId). Used to fan the report call out across every client they belong to.
  const membershipsForUser = (userId) =>
    clientMembers.filter((m) => String(m.userId) === String(userId));

  // Single source of truth for a Member's "All Clients" scope: every membership the
  // report should aggregate across. Until memberships resolve, fall back to the active
  // session membership so the first load isn't blank. Consumed by both the activity-type
  // prefetch and the report fan-out below so the fallback lives in exactly one place.
  const memberScopedMemberships = useMemo(() => {
    if (role !== ROLE_EMPLOYEE_MEMBER) return [];
    return memberClientsList.length > 0
      ? memberClientsList
      : [{ client: { id: readSessionCurrentClient()?.id ?? null }, id: memberId }];
  }, [role, memberClientsList, memberId]);

  // Fan a set of report calls out across clients/memberships and merge the rows into
  // one { data } payload, dropping duplicates. Shared by the roles that aggregate a
  // person or team across multiple clients. Each loader is a () => Promise<response>.
  const fanOutAndMerge = (loaders) =>
    Promise.all(
      loaders.map((load) =>
        load()
          .then((r) => (Array.isArray(r.data) ? r.data : []))
          .catch(() => [])
      )
    ).then((lists) => ({ data: dedupeById(lists.flat()) }));

  // The member ids a selected person holds, as owner-filter parameters. The Member dropdown
  // value is a userId, but the query filters on member_id (the _member PK) — passing the raw
  // userId would match no rows. A person can hold several memberships, hence a list.
  const memberOwnerParams = (userId) => {
    const ids = membershipsForUser(userId).map((m) => m.id);
    return { ownerIds: ids.length > 0 ? ids : [userId], ownerType: OWNER_TYPE_MEMBER };
  };

  // Resolves the current Apply to one EffortFilter. Each role contributes only the part of
  // the scope specific to it — a manager roots the query at their own manager records, an
  // admin at a client list — and everything else is shared.
  //
  // Replaces a per-role map of loaders that hit three different endpoints and, for the manager
  // and member paths, fanned a request out per manager record or per membership and merged the
  // responses in the browser. Returns null when there is nothing to scope by, so the caller
  // can skip the request rather than send an unscoped one.
  const buildEffortFilter = (ctx) => {
    const shared = { fromDate: ctx.from, toDate: ctx.to };
    // A blank selection means "no client scope yet", not a client whose id is empty: the latter
    // would travel as clientIds= and the backend rejects an empty list.
    const selectedClientIds =
      ctx.selectedClient == null || ctx.selectedClient === "" || ctx.selectedClient === ALL_CLIENTS
        ? null
        : [ctx.selectedClient];

    switch (role) {
      case ROLE_EMPLOYEE_MANAGER: {
        // Root at every manager record the user holds; their L1 children are expanded in SQL,
        // which is what the per-manager fan-out used to do in the browser.
        const managerIds = managerClients.map((c) => c.id).filter((id) => id != null);
        if (managerIds.length === 0) return null;

        const owner = ctx.isSubtreeManagerSelected && ctx.subtreeSelectedManagerId
          ? {
              // Resolved against the whole subtree, not the client-narrowed options: clientIds
              // below is what narrows, and it is intersected with these ids server-side.
              ownerIds: siblingRecordIds(subtreeManagers, ctx.subtreeSelectedManagerId),
              ownerType: OWNER_TYPE_MANAGER,
            }
          : ctx.isManagerSelfSelected
            ? { ownerIds: managerIds, ownerType: OWNER_TYPE_MANAGER }
            : ctx.effectiveMemberId
              ? memberOwnerParams(ctx.effectiveMemberId)
              : {};
        return { ...shared, managerIds, clientIds: selectedClientIds, ...owner };
      }

      case ROLE_EMPLOYEE_MEMBER: {
        // A member only ever sees themselves, across the memberships in scope.
        const memberships = memberScopedMemberships
          .filter((m) => m.client?.id != null)
          .filter((m) => ctx.selectedClient === ALL_CLIENTS
            || String(m.client.id) === String(ctx.selectedClient));
        const clientIds = memberships.map((m) => m.client.id);
        const ownerIds = memberships.map((m) => m.id).filter((id) => id != null);
        if (clientIds.length === 0) return null;
        return {
          ...shared,
          clientIds,
          ownerIds: ownerIds.length > 0 ? ownerIds : [memberId],
          ownerType: OWNER_TYPE_MEMBER,
        };
      }

      case ROLE_BDM:
      case ROLE_ADMIN: {
        if (ctx.isAdminManagerSelected && ctx.adminSelectedManagerId) {
          // Root at the selected manager so their subtree resolves, then keep only their own
          // rows — matching the previous behaviour of showing a manager's own efforts, not
          // their team's. Every assignment that person holds is named, so a manager working
          // for several clients reports their whole month rather than one client's slice.
          // Resolved against the full manager list; clientIds is what narrows to one client.
          const ownerIds = siblingRecordIds(adminManagers, ctx.adminSelectedManagerId);
          return {
            ...shared,
            managerIds: ownerIds,
            ownerIds,
            ownerType: OWNER_TYPE_MANAGER,
            clientIds: selectedClientIds,
          };
        }
        // Blank ids are filtered out before the emptiness check: with no clients resolved,
        // selectedClient is still null, so a naive [selectedClient] would be a length-1 list
        // of nothing and slip past the guard.
        const clientIds = (ctx.selectedClient === ALL_CLIENTS
          ? clients.map((c) => c.id)
          : [ctx.selectedClient]
        ).filter((id) => id != null && id !== "");
        // No clients to scope by (empty session and none returned from the backend): skip the
        // request rather than send an empty list, which the backend rejects.
        if (clientIds.length === 0) return null;
        return {
          ...shared,
          clientIds,
          ...(ctx.effectiveMemberId ? memberOwnerParams(ctx.effectiveMemberId) : {}),
        };
      }

      default: {
        // ROLE_CLIENT and anything else: the selected person scoped to the chosen client.
        if (!ctx.selectedClient || ctx.selectedClient === ALL_CLIENTS) return null;
        return {
          ...shared,
          clientIds: [ctx.selectedClient],
          ...(ctx.effectiveMemberId ? memberOwnerParams(ctx.effectiveMemberId) : {}),
        };
      }
    }
  };

  const dispatch = useDispatch();

  const initialLoadDoneRef = useRef(false);
  // True while report rows are being fetched, so the grid says it is loading rather than showing
  // "No rows". Each fetch takes a number; only the latest one clears the flag, so an older,
  // slower response can't end the loading state of a newer Apply. @since NEXT_VERSION
  const [reportLoading, setReportLoading] = useState(false);
  const reportRequestRef = useRef(0);
  const membersClientRef = useRef(undefined);

  let clientList;

  useEffect(() => {
    if (role === ROLE_EMPLOYEE_MANAGER) {
      const list = readSessionCurrentManager();
      setManagerClients(Array.isArray(list) ? list : []);
      setSelectedClient(ALL_CLIENTS);
    }

    if (role === ROLE_BDM) {
      // clients is already narrowed to this BDM's accounts, so "All Clients" below expands to
      // those and never to every client in the system.
      getBdmClients()
        .then((list) => {
          const arr = Array.isArray(list) ? list : [];
          setClients(
            arr.map((client) => ({
              id: client.id,
              email: client.contactPersonEmail,
              clientCode: client.code,
              clientName: client.name,
            }))
          );
          setSelectedClient(prevSelectedClient ? prevSelectedClient : ALL_CLIENTS);
        })
        .catch(() => setClients([]));
    }

    if (role === ROLE_ADMIN) {
      const applyClients = (list) => {
        if (!Array.isArray(list) || list.length === 0) return;
        setClients(
          list.map((client) => {
            return {
              id: client.id,
              email: client.contactPersonEmail,
              clientCode: client.code,
              clientName: client.name,
            };
          })
        );

        // Default to the whole-system view so an Admin sees all Managers and Members
        // across every client; a specific client can still be picked to filter.
        setSelectedClient(prevSelectedClient ? prevSelectedClient : ALL_CLIENTS);
      };

      clientList = readSessionCurrentClient();
      if (Array.isArray(clientList) && clientList.length > 0) {
        applyClients(clientList);
      } else {
        // Session has no cached clients: "All Clients" would send an empty
        // clientIds= (backend 422s). Fetch the list so the scope is never empty.
        loadAllClients()
          .then((response) => applyClients(response?.data))
          .catch((error) => handleAxiosError(error));
      }
    }

    if (role === ROLE_CLIENT) {
      const clientObj = readSessionCurrentClient();
      setSelectedClient(clientObj?.id != null ? String(clientObj.id) : null);
    }

    if (role === ROLE_EMPLOYEE_MEMBER) {
      const member = readSessionCurrentMember();
      setMemberId(parseNullableInt(member?.id));
      // Default to "All" synchronously so the client dropdown renders on first
      // paint (no late pop-in). The membership options populate once they load.
      setSelectedClient(ALL_CLIENTS);
      getMemberClients().then((list) => {
        setMemberClientsList(Array.isArray(list) ? list : []);
        setMemberClientsLoaded(true);
      });
    }
  }, []);

  useEffect(() => {
    if (clients.length > 0) {
      setSelectedClient(prevSelectedClient ? prevSelectedClient : ALL_CLIENTS);
    }
  }, [clients]);

  // Load every manager once (Admin only). They populate the Member dropdown as
  // selectable Manager options and feed the grid's Client-name lookup.
  useEffect(() => {
    if (!isClientScoped) return;
    loadAllManagersFromBackend()
      .then((response) => {
        const all = response.status === 204 || !Array.isArray(response.data) ? [] : response.data;
        // A BDM may only offer the managers of their own accounts. The endpoint returns every
        // manager, so the narrowing happens here -- without it the Member / Manager dropdown
        // would list people at clients this BDM has no claim on.
        if (role !== ROLE_BDM) {
          setAdminManagers(all);
          return;
        }
        const ownClientIds = new Set(clients.map((c) => String(c.id)));
        setAdminManagers(all.filter((m) => ownClientIds.has(String(m.clientId))));
      })
      .catch(() => setAdminManagers([]));
  }, [role, isClientScoped, clients]);

  // Load the subtree from ALL the manager's records, not just the first — an L1 may
  // report through any of them. An L1 gets just themselves.
  useEffect(() => {
    if (role !== ROLE_EMPLOYEE_MANAGER) return;
    const managerIds = managerClients.map((c) => c.id).filter((id) => id != null);
    if (managerIds.length === 0) return;
    loadScopeManagersForIds(managerIds)
      .then((list) => setSubtreeManagers(list))
      .catch(() => setSubtreeManagers([]));
  }, [role, managerClients]);

  // Resolve the logged-in manager's manager_id once. Required for the manager-scoped
  // report endpoint; if it never resolves, fall back to the client-scoped endpoint.
  useEffect(() => {
    if (role !== ROLE_EMPLOYEE_MANAGER) return;
    let cancelled = false;
    getCurrentManager()
      .then((manager) => {
        if (!cancelled) {
          setSelfManagerId(parseNullableInt(manager?.id));
          setSelfManagerName(manager?.name || "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelfManagerId(null);
          setSelfManagerName("");
        }
      });
    return () => { cancelled = true; };
  }, [role]);

  const btnClickHandler = useCallback(async () => {
    const { from: startDate, to: endDate } = periodRange(period);

    dispatch(reportActions.replaceStartDate(startDate));
    dispatch(reportActions.replaceEndDate(endDate));
    dispatch(reportActions.replaceSearchOption(period.searchOption));
    dispatch(reportActions.replaceYear(period.year));
    dispatch(reportActions.replaceWeek(period.fiscalWeek));
    dispatch(reportActions.replaceMonth(period.month));
    dispatch(reportActions.replaceClient(selectedClient));
    dispatch(reportActions.replaceMember(selectedMember));

    if (clients.length > 0 && !selectedClient) {
      setSelectedClient(clients[0].id);
    }

    // Manager selecting their own "(Me)" option.
    const isManagerSelfSelected =
      role === ROLE_EMPLOYEE_MANAGER && selectedMember === MANAGER_SELF_OPTION_VALUE;

    // Admin selecting a specific Manager from the Member dropdown (namespaced value).
    const isAdminManagerSelected =
      isClientScoped &&
      typeof selectedMember === "string" &&
      selectedMember.startsWith(MANAGER_OPTION_PREFIX);
    const adminSelectedManagerId = isAdminManagerSelected
      ? parseNullableInt(selectedMember.slice(MANAGER_OPTION_PREFIX.length))
      : null;

    // Manager (L2) selecting one of their L1 children from the Managers optgroup.
    const isSubtreeManagerSelected =
      role === ROLE_EMPLOYEE_MANAGER &&
      typeof selectedMember === "string" &&
      selectedMember.startsWith(MANAGER_OPTION_PREFIX);
    const subtreeSelectedManagerId = isSubtreeManagerSelected
      ? parseNullableInt(selectedMember.slice(MANAGER_OPTION_PREFIX.length))
      : null;

    // The active member filter: members are scoped to themselves; everyone else uses the
    // Member / Manager dropdown, where "All" resolves to null. The manager-self,
    // admin-manager, and subtree-manager sentinels must not leak into the client-scoped
    // endpoint as a member id, so they resolve to null too.
    const effectiveMemberId =
      role === ROLE_EMPLOYEE_MEMBER
        ? memberId
        : isManagerSelfSelected ||
            isAdminManagerSelected ||
            isSubtreeManagerSelected ||
            selectedMember === ALL_USERS
          ? null
          : selectedMember || null;

    // For Admin, "All Clients" expands to every client id; a specific client narrows it.
    const adminClientScope =
      selectedClient === ALL_CLIENTS ? clients.map((c) => c.id).join(",") : selectedClient;

    const managerAllMembers = isManagerSelfSelected || !effectiveMemberId;
    const from = getDateInFormat(startDate);
    const to = getDateInFormat(endDate);

    // Refresh the activity-type master list for the applied client(s) so the
    // report's Sub-Type column labels stay in sync with the rows. Done here (on
    // Apply / refresh), not on dropdown change, so the column updates together
    // with the rest of the table.
    const resolveTypeClientIds = () => {
      // A specific client is selected → just that one (or none).
      if (selectedClient !== ALL_CLIENTS) {
        return selectedClient ? [selectedClient] : [];
      }
      // "All Clients" expands differently per role.
      switch (role) {
        case ROLE_BDM:
        case ROLE_ADMIN:
          return clients.map((c) => c.id);
        case ROLE_EMPLOYEE_MANAGER:
          // Every client in the manager's scope (subtree + own assignments), so the
          // Sub-Type column has type labels for rows from an independent assignment too.
          return mergeManagerScope(subtreeManagers, managerClients)
            .map((m) => m.clientId)
            .filter((id) => id != null);
        case ROLE_EMPLOYEE_MEMBER:
          return memberScopedMemberships.map((m) => m.client?.id).filter((id) => id != null);
        default:
          return [];
      }
    };
    const typeClientIds = resolveTypeClientIds();
    // Prefer the cache warmed on dashboard load; only hit the network for a
    // client whose types aren't cached yet, and cache the result for next time.
    const typesForClient = (id) => {
      const cached = typesByClient[String(id)];
      if (cached) return Promise.resolve(cached);
      return loadAllActivityTypesByClient(id)
        .then((r) => {
          const data = r.data || [];
          dispatch(clientActions.cacheTypesForClient({ clientId: id, data }));
          return data;
        })
        .catch(() => []);
    };
    const typesPromise =
      typeClientIds.length > 0
        ? Promise.all(typeClientIds.map(typesForClient)).then((lists) => dedupeById(lists.flat()))
        : Promise.resolve(null);

    // The resolved filter context for this Apply, handed to the role's loader.
    const reportContext = {
      from,
      to,
      selectedClient,
      effectiveMemberId,
      managerAllMembers,
      isManagerSelfSelected,
      isAdminManagerSelected,
      adminSelectedManagerId,
      adminClientScope,
      isSubtreeManagerSelected,
      subtreeSelectedManagerId,
    };
    const effortFilter = buildEffortFilter(reportContext);
    const request = ++reportRequestRef.current;
    setReportLoading(true);
    const loadPromise = effortFilter === null
      ? Promise.resolve({ data: [] })
      : loadUnifiedEfforts(effortFilter);

    await Promise.all([loadPromise, typesPromise])
      .then(([response, types]) => {
        // A 204 No Content response carries an empty body (response.data === ""),
        // so normalise to an array before treating it as report rows.
        let reportRows = Array.isArray(response.data) ? response.data : [];
        // De-dupe by row id before anything downstream sees the rows. The holiday
        // cross-join fans a holiday across every membership, so a person holding two
        // _member records for one client yields the same HOL- id twice. Duplicate row
        // ids break the grid's row reconciliation — the previous Apply's rows leak into
        // the next view (e.g. April rows lingering after switching to May) — and also
        // double-count hours in the footer totals below.
        reportRows = dedupeById(reportRows);
        // Land the new activity types and rows in the same React batch so the
        // Sub-Type column never maps the old rows against the new client's types
        // (which briefly blanked those cells before the rows were replaced).
        if (types != null) dispatch(clientActions.replaceTypeData(types));
        dispatch(reportActions.replaceData([]));
        dispatch(reportActions.replaceData(reportRows));
        var hrs = 0;
        var expectedHours = 0;
        // Counted once per person, not once per row and not once per memberId: _member is unique on
        // (user_id, client_id), so somebody mapped to two clients owns two member ids and used to
        // contribute their monthly expectation twice (HRMS-254). personKey is the same value on
        // every row a person owns, whichever client the row belongs to.
        const countedPersons = new Set();
        reportRows.forEach((effort) => {
          const personKey = effort.personKey ?? `m:${effort.memberId}`;
          if (!countedPersons.has(personKey)) {
            countedPersons.add(personKey);
            expectedHours = expectedHours + effort.expectedHours;
          }
          hrs = hrs + effort.hoursSpent;
        });
        dispatch(reportActions.replaceTotalHours(hrs));
        dispatch(reportActions.replaceExpectedHours(expectedHours));
      })
      .catch((error) => {
        dispatch(reportActions.replaceData([])); // Set the empty data in Consolidated Report table
        dispatch(
          reportActions.replaceExpectedHours(0)
        );
        dispatch(
          reportActions.replaceTotalHours(0)
        );
        handleAxiosError(error);
      })
      .finally(() => {
        if (request === reportRequestRef.current) setReportLoading(false);
      });
    // };
  }, [
    selectedClient,
    selectedMember,
    period,
    selfManagerId,
    role,
    memberId,
    clients,
    managerClients,
    subtreeManagers,
    adminManagers,
    memberClientsList,
    memberScopedMemberships,
    clientMembers,
    typesByClient,
    dispatch,
  ]);

  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    if (role === ROLE_EMPLOYEE_MANAGER) {
      if (managerClients.length === 0) return;
    } else if (role === ROLE_EMPLOYEE_MEMBER) {
      // Wait until memberships resolve. With memberships we load across them;
      // with none, fall back to the session membership (which needs a memberId).
      if (!memberClientsLoaded) return;
      if (memberClientsList.length === 0 && !memberId) return;
    } else if (isClientScoped) {
      // Wait until the client list is known (session cache, on-demand fetch, or the BDM's own
      // accounts), so "All Clients" resolves to a real scope instead of an empty clientIds param.
      if (clients.length === 0) return;
    } else if (!selectedClient) {
      return;
    }
    initialLoadDoneRef.current = true;
    btnClickHandler();
  }, [selectedClient, memberId, role, managerClients, memberClientsList, memberClientsLoaded, clients, btnClickHandler]);

  useEffect(() => {
    if (role === ROLE_EMPLOYEE_MEMBER) return;
    if (!selectedClient) return;

    // Reset the member filter to "All" when the client actually changes,
    // but preserve the restored selection on the initial load.
    if (membersClientRef.current !== undefined && membersClientRef.current !== selectedClient) {
      setSelectedMember(ALL_USERS);
    }
    membersClientRef.current = selectedClient;

    if (isClientScoped && selectedClient === ALL_CLIENTS) {
      const ownClientIds = new Set(clients.map((c) => String(c.id)));
      loadMembersForAllClientsFromBackend()
        .then((r) => {
          const all = r.status === 204 || !Array.isArray(r.data) ? [] : r.data;
          // Same narrowing as the manager list above: "All Clients" means all of THIS BDM's
          // clients, so members from anywhere else must not reach the dropdown.
          setClientMembers(
            role === ROLE_BDM
              ? all.filter((m) => ownClientIds.has(String(m.client?.id ?? m.clientId)))
              : all
          );
        })
        .catch(() => setClientMembers([]));
      return;
    }

    // Members per client are cached (warmed on dashboard load). A cache miss
    // fetches once and stores the result, so later dropdown changes don't re-fetch.
    const cachedMembers = (clientId) => membersByClient[String(clientId)];
    const fetchAndCacheMembers = (clientId, request) =>
      request
        .then((r) => {
          const list = r.status === 204 ? [] : r.data;
          dispatch(clientActions.cacheMembersForClient({ clientId, data: list }));
          return list;
        });

    // A manager's Member dropdown lists only the members reporting to that manager,
    // not every member of the client. Use the manager-scoped endpoint (keyed by the
    // manager's manager_id for each client), aggregating across clients for "All".
    if (role === ROLE_EMPLOYEE_MANAGER) {
      // A single client can be run by SEVERAL managers in scope (the L2 and its L1s); the
      // members dropdown must aggregate each manager's members. Scope = the subtree plus
      // the manager's own assignments, so an independent assignment's members show too.
      const subtreeScope = mergeManagerScope(subtreeManagers, managerClients);
      const managersInScope =
        selectedClient === ALL_CLIENTS
          ? subtreeScope
          : subtreeScope.filter((m) => String(m.clientId) === String(selectedClient));
      if (managersInScope.length === 0) {
        setClientMembers([]);
        return;
      }
      const fetchMembersFor = (m) =>
        loadMembersFromBackendForManager(m.id)
          .then((r) => (r.status === 204 || !Array.isArray(r.data) ? [] : r.data))
          .catch(() => []);
      // Group by client: a client with ONE manager can use the warmed per-client cache; a
      // client with several managers must fetch each (the per-client cache holds only one
      // manager's members and can't represent the whole set).
      const byClient = managersInScope.reduce((acc, m) => {
        (acc[m.clientId] = acc[m.clientId] || []).push(m);
        return acc;
      }, {});
      Promise.all(
        Object.entries(byClient).flatMap(([clientId, mgrs]) => {
          if (mgrs.length === 1) {
            const cached = cachedMembers(clientId);
            return [cached ? Promise.resolve(cached) : fetchAndCacheMembers(clientId, loadMembersFromBackendForManager(mgrs[0].id)).catch(() => [])];
          }
          return mgrs.map(fetchMembersFor);
        })
      ).then((lists) => setClientMembers(dedupeById(lists.flat())));
      return;
    }

    const cached = cachedMembers(selectedClient);
    if (cached) {
      setClientMembers(cached);
      return;
    }
    fetchAndCacheMembers(selectedClient, loadMembersFromBackendForClient(selectedClient))
      .then((list) => setClientMembers(list))
      .catch((error) => {
        setClientMembers([]);
        handleAxiosError(error);
      });
    // clients is a dependency because the BDM branch above narrows by it: the account list resolves
    // asynchronously, so an effect that did not re-run once it arrived would filter every member
    // out against an empty set and leave the dropdown permanently blank.
  }, [selectedClient, role, isClientScoped, clients, managerClients, subtreeManagers, membersByClient, dispatch]);

  const editEventHandler = async (data) => {
    setCopyObject(null);
    setActivityBulkMode(false);
    if (data.effortType === "ACTIVITY") {
      setEditObject(data);
      setAddActivityOpen(true);
      return;
    }
    // The report row omits leaveDuration (it's dropped during effort aggregation).
    // Fetch the plan first so the leave dialog opens already showing the saved
    // half-day selection instead of flashing Full Day and then correcting itself.
    const leavePlanId = parseReportRowEntityId(data.id);
    let leaveEditObject = data;
    if (leavePlanId) {
      try {
        const res = await getLeavePlanById(leavePlanId);
        if (res?.data?.leaveDuration) {
          leaveEditObject = { ...data, leaveDuration: res.data.leaveDuration };
        }
      } catch {
        // Keep the dialog's default duration; still open so the user can edit.
      }
    }
    setEditObject(leaveEditObject);
    setAddLeaveOpen(true);
  };

  const copyEventHandler = (data) => {
    setEditObject(null);
    setActivityBulkMode(false);
    setCopyObject({
      memberId: data.memberId,
      clientId: data.clientId,
      activityTypeId: data.activityTypeId,
      // Carried over so a copied activity keeps its project. The dialog drops it if the client
      // no longer offers that project. @since NEXT_VERSION
      projectId: data.projectId,
      location: data.location,
      clientTicketRef: data.clientTicketRef,
      description: data.description,
      isMember: data.isMember,
    });
    setAddActivityOpen(true);
  };

  const confirmDeleteDialogHandler = (effortType, data) => {
    if (!data?.id) {
      return;
    }

    if (effortType === "ACTIVITY")
      setConfirmationMessage(
        `Are you sure you want to delete this Activity (Start Date: '${data.startDate}', Hours: '${
          data.hoursSpent
        }', Description: '${data.description?.substring(0, 15)}...')?`
      );
    else if (effortType === "HOLIDAY")
      setConfirmationMessage(
        `Are you sure you want to remove this Optional Holiday (Date: '${data.startDate}', Holiday: '${data.description}')? It will be added back to the optional holiday quota.`
      );
    else
      setConfirmationMessage(
        `Are you sure you want to delete this Leave Plan (Start Date: '${
          data.startDate
        }', Reason: '${data.description?.substring(0, 15)}...')?`
      );

    const editObjectId = parseReportRowEntityId(data.id);
    if (!editObjectId) {
      return;
    }
    setDeleteId(editObjectId);
    // Mirrors EffortService.ownerKey on the server (!Boolean.FALSE.equals), which is what
    // resolved this row's election in the first place, so both agree on the owner scope.
    setDeleteOwner({ ownerId: data.memberId, isMember: data.isMember !== false });
    setObjectType(effortType);
    setConfirmOpen(true);
  };

  const deleteActivityFromBackend = async () => {
    await deleteActivity(deleteId)
      .then((response) => {
        track(FEATURE_ACTIVITY_DELETED);
        btnClickHandler();
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const deleteLeavePlanFromBackend = async () => {
    await deleteLeavePlan(deleteId)
      .then((response) => {
        btnClickHandler();
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  // Removes the member's election so the day stops counting as an availed optional holiday
  // and returns to their quota. The holiday itself is master data and is left untouched.
  const withdrawOptionalHolidayFromBackend = async () => {
    await withdrawHolidayElectionInBackend(deleteId, deleteOwner?.ownerId, deleteOwner?.isMember)
      .then(() => {
        btnClickHandler();
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const deleteHandler = () => {
    if (objectType === "ACTIVITY") deleteActivityFromBackend();
    else if (objectType === "HOLIDAY") withdrawOptionalHolidayFromBackend();
    else deleteLeavePlanFromBackend();
  };

  // Every client the manager touches (own assignments unioned with the subtree's, incl. the
  // L1s'), and the L1s offered in the Managers group — the L2's own records are excluded
  // because "(Me)" covers them.
  const { clientOptions: managerClientOptions, subtreeL1Options } = useManagerScopeOptions({
    subtreeManagers: role === ROLE_EMPLOYEE_MANAGER ? subtreeManagers : [],
    managerClients,
    selectedClient,
  });

  // Managers offered in the Admin Member dropdown: all of them for "All Clients",
  // otherwise just the managers of the selected client.
  const adminManagerOptions =
    isClientScoped
      ? selectedClient === ALL_CLIENTS
        ? adminManagers
        : adminManagers.filter((m) => String(m.clientId) === String(selectedClient))
      : [];

  // The Member / Manager dropdown. Members carry their userId, which the report call resolves
  // back to the right membership id(s) per client; managers carry the namespaced manager id.
  const { memberOptions, managerOptions, selfOption } = useUserFilterOptions({
    members: clientMembers,
    managers: isClientScoped ? adminManagerOptions : subtreeL1Options,
    self:
      role === ROLE_EMPLOYEE_MANAGER && selfManagerId
        ? { id: selfManagerId, name: selfManagerName }
        : null,
  });

  // Which client list this role picks from. A client user has no choice to make, and a member
  // picks from their own memberships; the other two pick from a fetched list.
  const clientFilterOptions =
    role === ROLE_EMPLOYEE_MANAGER
      ? managerClientOptions
      : role === ROLE_EMPLOYEE_MEMBER
        ? toMemberClientOptions(memberClientsList)
        : toClientListOptions(clients);

  // clientId -> client NAME, so the report grid's Client column shows the client
  // name (not a code or raw id). Covers the manager/member/admin sources.
  const clientLabelById = {};
  managerClients.forEach((c) => {
    clientLabelById[String(c.clientId)] = c.clientName ?? String(c.clientId);
  });
  memberClientsList.forEach((m) => {
    if (m?.client?.id != null) {
      clientLabelById[String(m.client.id)] = m.client.name ?? String(m.client.id);
    }
  });
  clients.forEach((c) => {
    clientLabelById[String(c.id)] = c.clientName ?? String(c.id);
  });
  adminManagers.forEach((m) => {
    if (m?.clientId != null && clientLabelById[String(m.clientId)] == null) {
      clientLabelById[String(m.clientId)] = m.clientName ?? String(m.clientId);
    }
  });
  subtreeManagers.forEach((m) => {
    if (m?.clientId != null && clientLabelById[String(m.clientId)] == null) {
      clientLabelById[String(m.clientId)] = m.clientName ?? String(m.clientId);
    }
  });

  return (
    <Card>
      <MDBox
        mx={2}
        mt={-3}
        py={1}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Consolidated Report
        </MDTypography>
      </MDBox>
      <MDBox mt={3} p={2}>
        {/* A client user has no client to choose, and a team member only ever sees
            themselves — both filters are dropped for those roles rather than shown empty. */}
        <EffortFilterBar
          idPrefix="report"
          showClient={role !== ROLE_CLIENT}
          clientValue={selectedClient}
          onClientChange={setSelectedClient}
          clientOptions={clientFilterOptions}
          showMemberManagerDropdown={role !== ROLE_EMPLOYEE_MEMBER}
          userValue={selectedMember}
          onUserChange={setSelectedMember}
          memberOptions={memberOptions}
          managerOptions={managerOptions}
          selfOption={selfOption}
          groupUsers={isClientScoped || role === ROLE_EMPLOYEE_MANAGER}
          period={period}
          onPeriodChange={setPeriod}
          onAction={btnClickHandler}
          actionLabel="Search"
        />
      </MDBox>
      {role !== ROLE_CLIENT && !isClientScoped && (
        <MDBox p={2}>
          {/* The two activity buttons are one group, set apart from Add Leave by the wider outer
              gap: both open the same dialog, one activity at a time or several.
              @since NEXT_VERSION */}
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditObject(null);
                  setCopyObject(null);
                  setActivityBulkMode(false);
                  setAddActivityOpen(true);
                }}
                sx={{ width: { xs: "100%", sm: 180 }, m: 0 }}
                variant="contained"
                color="success"
              >
                Add Activity
              </Button>
              {/* @since NEXT_VERSION */}
              <Button
                startIcon={<AddMultipleIcon />}
                onClick={() => {
                  setEditObject(null);
                  setCopyObject(null);
                  setActivityBulkMode(true);
                  setAddActivityOpen(true);
                }}
                sx={{ width: { xs: "100%", sm: 240 }, m: 0 }}
                variant="contained"
                color="success"
              >
                Add Multiple Activities
              </Button>
            </Stack>
            <Button
              startIcon={<TimelapseIcon />}
              onClick={() => {
                setEditObject(null);
                setCopyObject(null);
                setAddLeaveOpen(true);
                track(FEATURE_ADD_LEAVE);
              }}
              sx={{ width: { xs: "100%", sm: 150 }, m: 0 }}
              variant="contained"
              color="success"
            >
              Add Leave
            </Button>
          </Stack>
        </MDBox>
      )}
      <ReportDataGridBase
        reportData={reportData}
        loading={reportLoading}
        role={role}
        currentManagerIds={selfManagerIds}
        totalHours={totalHours}
        expectedWorkingHours={expectedWorkingHours}
        clientLabelById={clientLabelById}
        confirmDeleteDialogHandler={confirmDeleteDialogHandler}
        editEventHandler={editEventHandler}
        copyEventHandler={copyEventHandler}
        refreshDataHandler={btnClickHandler}
        displayAppovalBtn={role === ROLE_EMPLOYEE_MANAGER || role === "ADMIN"}
        memberFlag={role === ROLE_EMPLOYEE_MANAGER || role === "ADMIN"}
        showApprovalCounts
      />
      <ConfirmDialog
        title={DELETE_DIALOG_TITLES[objectType] ?? DELETE_DIALOG_TITLES.LEAVE_PLAN}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
      <AddActivityDialog
        role={role}
        addActivityOpen={addActivityOpen}
        setAddActivityOpen={setAddActivityOpen}
        refreshActivitiesDataHandler={btnClickHandler}
        editObject={editObject}
        copyObject={copyObject}
        bulkMode={activityBulkMode}
        membersByClient={membersByClient}
        typesByClient={typesByClient}
      />
      <AddLeaveDialog
        role={role}
        addLeaveOpen={addLeaveOpen}
        setAddLeaveOpen={setAddLeaveOpen}
        refreshActivitiesDataHandler={btnClickHandler}
        editObject={editObject}
        membersByClient={membersByClient}
      />
    </Card>
  );
};
